import { connect } from 'react-redux';
import React from 'react';
import PropTypes from 'prop-types';
import { usePrefs, useRequestData, useLabels } from 'wsm-common-data';
import { ErrorBoundary } from 'wsm-error-boundary';
import logger from 'ws-scripts/modules/logger';
import { isBrowser } from 'ws-scripts/modules/environment';
import { setClassNames } from 'ddc-classnames-js';
import CTASection from '../components/CTASection';
import Error from '../components/Error';
import VehicleCard from '../components/VehicleCard';
import { UNKNOWN_ERROR } from '../server/namespaces';
import '../global-sass/main.scss';

function setSharedData(offer) {
	if (isBrowser && offer?.vehicle?.chromeStyleId) {
		// Adding chrome style ID to WidgetData for use in recommended vehicles.
		// When we don't have browsing history for a user, this is used as a recommendation seed vehicle.
		window.DDC = window.DDC || {};
		window.DDC.WidgetData = window.DDC.WidgetData || {};
		window.DDC.WidgetData['ws-amp-offer'] =
			window.DDC.WidgetData['ws-amp-offer'] || {};
		window.DDC.WidgetData['ws-amp-offer'].offerChromeStyleId =
			offer.vehicle.chromeStyleId;
		window.DDC.WidgetData['ws-amp-offer'].offerVehicleType =
			offer.vehicle?.type === 'N' ? 'NEW' : 'USED';
	}
}

function WidgetContainer({ offerData }) {
	const { widgetBackground } = usePrefs();
	const { deviceType } = useRequestData();
	const isDesktop = deviceType === 'DESKTOP';
	const showErrorComponent = !!offerData.ssrError;
	const showTradeVerbiage = offerData.trade?.vehicle.canTradeVehicle;
	const widgetContainerClassesVerticalPadding = showErrorComponent
		? 'py-7'
		: 'mt-n5';
	const widgetContainerClasses = [
		'mx-n4',
		widgetBackground === 'none' ? '' : widgetBackground,
		isDesktop
			? 'row mb-5 d-flex justify-content-center'
			: `${widgetContainerClassesVerticalPadding} bg-white`
	];
	const mainSectionContainerMobile = `mx-10${
		showErrorComponent ? ' bg-white border' : ''
	}`;
	const mainSectionContainer = [
		'main-section-container',
		'text-center',
		isDesktop
			? 'my-15 px-9 py-5 bg-white border'
			: mainSectionContainerMobile
	];
	const mainTitle = [
		'font-weight-normal',
		'text-left',
		'text-black',
		isDesktop ? 'ddc-font-size-xxlarge' : 'ddc-font-size-xxxlarge mb-4 pt-6'
	];

	setSharedData(offerData.offer);
	const labels = useLabels();
	const mainParagraphClasses = ['main-paragraph text-left mb-6 text-black'];
	const inlineMainSectionContainer = { width: isDesktop ? '780px' : '' };
	const inlineMainParagraph = { lineHeight: '25px' };

	return (
		<ErrorBoundary
			errorMarkup={
				<Error
					offerData={{
						ssrError: UNKNOWN_ERROR
					}}
				/>
			}
			errorHandler={(error, errorInfo) => {
				const newError = new Error(
					`Encountered an error in ws-amp-offer.\n${error}`
				);
				newError.originalError = error;
				newError.originalStackTrace = errorInfo.componentStack;
				logger.error(`${newError}\n${newError.originalStackTrace}`);
			}}
		>
			<div className={setClassNames(widgetContainerClasses)}>
				<div
					className={setClassNames(mainSectionContainer)}
					style={inlineMainSectionContainer}
				>
					{!showErrorComponent ? (
						<React.Fragment>
							<h1 className={setClassNames(mainTitle)}>
								{labels.get('YOUR_PERSONALIZED_OFFER')}
							</h1>
							<h2 className="text-left font-weight-normal text-black">
								{labels.get('HI')}{' '}
								{offerData.customer.firstName}!
							</h2>
							<div
								className={setClassNames(mainParagraphClasses)}
								style={inlineMainParagraph}
							>
								{showTradeVerbiage
									? labels.get('OUR_RECORDS')
									: labels.get('NO_TRADE_IN_VALUE_START')}
							</div>
							<VehicleCard offerData={offerData} />
							<CTASection offerData={offerData} />
						</React.Fragment>
					) : (
						<Error offerData={offerData} />
					)}
				</div>
			</div>
		</ErrorBoundary>
	);
}

WidgetContainer.propTypes = {
	offerData: PropTypes.shape()
};

const mapStateToProps = (state) => {
	return {
		offerData: state.offerData
	};
};

const Widget = connect(mapStateToProps, {})(WidgetContainer);

export default Widget;
